/**
 * VEC-3313 / VEC-3781 — declaración de los filtros de la grilla de Stock por Depósito para
 * <GridFilterPanel />.
 *
 * Vive en su propio módulo porque lo consumen DOS lados: `InventarioStockGridFiltros` (para
 * renderizar) e `InventarioStockGrid` (para calcular el query por defecto con las MISMAS reglas de
 * serialización que aplica el panel). Es el patrón de `movilesFilterFields.js` /
 * `ticketsFilterFields.js`; ver docs/design-system/grid-filter-panel.md.
 *
 * ALCANCE: cinco controles. El depósito y los tres de texto son de VEC-3313 Sc. 01; el de
 * Producto/Repuesto lo SUMA VEC-3781 Sc. 04, sin sacar ninguno de los anteriores. Categoría,
 * subcategoría, marca y estado de alerta los ACEPTA el endpoint (ItemStockValidation::getGrid) pero
 * NO se declaran acá: no los pide ningún scenario y sumarlos sería alcance que la card no pidió. El
 * día que se pidan, es agregar entradas a esta lista y nada más — el backend ya está.
 *
 * UNA CLAVE DEL QUERY NO ES UN CAMPO DEL PANEL: `ubicacionType`, que lo agrega la grilla. La
 * ubicación viaja como PAR (tipo + id) porque la columna de la base es polimórfica, y el backend
 * rechaza el par a medias con 400 (ItemStockService::normalizarUbicacion).
 *
 * VEC-3316 — DESDE QUE HAY DOS TIPOS (almacén y base), EL TIPO VIAJA DENTRO DEL VALOR de la opción:
 * "almacen:6", "base:1161". Antes el tipo no era una decisión del usuario —el único era 'almacen'—
 * y la grilla lo agregaba fijo; ahora un id suelto ya no identifica nada, porque el 6 puede ser el
 * almacén 6 o la base 6. `InventarioStockGrid::getQuery()` parte el valor con
 * `inventarioUbicaciones.parsear()` y manda las dos claves. Sigue sin haber un control propio para
 * el tipo, que es el punto: el usuario elige una ubicación, no un modelo de datos.
 */

export const INVENTARIO_STOCK_FILTROS_STORAGE_KEY = 'InventarioStockGrid_filtros';

/**
 * La grilla abre SIN filtros: el consolidado de todos los depósitos, que es lo que el Scenario 01
 * describe como estado inicial ("el detalle por depósito" se obtiene al filtrar). Se expone igual
 * como función para que la grilla y el panel usen la misma fuente y el reset tenga a dónde volver.
 */
export const getInventarioStockFilterDefaults = () => ({});

const ANCHO_MEDIO = { xs: 12, sm: 6, md: 3, lg: 3 };

/**
 * VEC-3781 Scenario 04 — el filtro de Producto/Repuesto.
 *
 * @param {{onSearch?: Function, onResolve?: Function}} item callbacks del `pastableMultiselect`.
 *        Los inyecta el componente de filtros, que es quien sabe pegarle al backend; acá solo se
 *        declara la forma del campo. Mismo patrón que `buildTicketsFilterFields(columnas, entidades)`.
 */
export const buildInventarioStockFilterFields = (item) => [
	{
		// `select` y no `multiselect`: el endpoint valida `ubicacionId` como id ÚNICO
		// (ItemStockValidation::idFiltro). Es a propósito — las tres columnas de stock son la
		// existencia EN una ubicación, y sumar dos ubicaciones en una fila no tiene lectura útil.
		// (El historial global de VEC-3315 sí lo acepta múltiple: ahí cada fila es un movimiento.
		// Pero ojo: ese endpoint acepta UN tipo con N ids, así que mezclar bases y almacenes en esa
		// pantalla necesita extender su contrato. No es el caso de acá.)
		//
		// VEC-3316: la etiqueta pasó de "Depósito" a "Ubicación" porque el desplegable ya no ofrece
		// solo almacenes del Pañol. "Depósito" es además la palabra que el usuario asocia con esos
		// almacenes, así que dejarla habría hecho que las bases se leyeran como depósitos.
		name: 'ubicacionId',
		type: 'select',
		messageId: 'inventarioStock.grid.filtros_ubicacion',
		label: 'Ubicación',
		size: ANCHO_MEDIO,
	},
	{
		// VEC-3781 Sc. 04 — el filtro de Producto/Repuesto. Se SUMA a los tres campos de texto que
		// ya estaban (Nº de parte, Código externo y Descripción), que quedan tal cual: la card pide
		// "el nuevo filtro", no reemplazar los que había. Los tres siguen sirviendo para acotar por
		// un campo puntual; este es para ir directo a un ítem que ya se sabe cuál es.
		//
		// `pastableMultiselect` y no `select`: los `select` del panel se alimentan de una lista
		// PRECARGADA (`options[name]`), y el catálogo crece con los datos del cliente. Es el caso
		// exacto que docs/design-system/grid-filter-panel.md manda resolver con este tipo, que busca
		// en el servidor mientras se tipea. El comentario de `inventarioMovimientosFilterFields.js`
		// ya anticipaba esta migración: "cuando ese endpoint se arregle, esto pasa a ser un
		// pastableMultiselect". El endpoint es /inventario/items/select, de VEC-3314.
		//
		// Manda DOS claves: `item` con los chips resueltos e `itemBusqueda` con el texto que quedó
		// sin resolver. Son los mismos nombres que usa el historial global de VEC-3315, con una
		// diferencia deliberada: allá `item` es un id suelto y acá es lista, porque este control es
		// múltiple. El backend las combina entre sí con OR dentro de un único where() agrupado.
		name: 'item',
		textName: 'itemBusqueda',
		type: 'pastableMultiselect',
		messageId: 'inventarioStock.grid.filtros_item',
		label: 'Producto/Repuesto',
		size: ANCHO_MEDIO,
		onSearch: (item || {}).onSearch,
		onResolve: (item || {}).onResolve,
	},
	{
		name: 'numeroDeParte',
		type: 'text',
		messageId: 'inventarioStock.grid.filtros_numero_de_parte',
		label: 'Nº de parte',
		size: ANCHO_MEDIO,
	},
	{
		name: 'externalCode',
		type: 'text',
		messageId: 'inventarioStock.grid.filtros_codigo_externo',
		label: 'Código externo',
		size: ANCHO_MEDIO,
	},
	{
		name: 'descripcion',
		type: 'text',
		messageId: 'inventarioStock.grid.filtros_descripcion',
		label: 'Descripción',
		size: ANCHO_MEDIO,
	},
];
